import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";

import { ApiURLs } from "../../constant/ApiUrls";
import { fetchData, postData } from "../../services/api";

/**
 * Advert features: which tick boxes a seller is shown, for each type of vehicle.
 *
 * Before this the site had one list for cars and, briefly, a second for bikes.
 * A motorbike seller was asked about electric windows and Isofix; a Track Bike
 * seller and an Adventure seller were asked the same 50 questions even though
 * they share almost none. This is that fixed, and made editable.
 *
 * Two rules the page is built around, both of which come from how a ticked
 * feature is stored - as a bare number on the advert, with nothing recording
 * which list it came from:
 *
 *  1. A feature is ONE row no matter how many types offer it. "ABS" on a Naked
 *     and "ABS" on a Sports are the same id, which is what makes a buyer's ABS
 *     filter find both. Adding a feature whose name already exists reuses it
 *     rather than making a second one.
 *
 *  2. Nothing is ever deleted. Taking a feature out of a type removes it from
 *     that list only; the feature keeps its id, and every advert that already
 *     ticked it still shows it. Retiring a feature entirely stops it being
 *     offered and leaves those adverts alone. The count beside each feature is
 *     how many live adverts have it ticked, so nobody removes one blind.
 */

const SECTION_LABELS = {
    "*": "All other vehicles (cars, vans, trucks...)",
    "motorbikes": "Motorbikes",
    "scooters": "Scooters",
    "vintage-bikes": "Vintage Bikes",
    "campers": "Campers & Motorhomes",
};

const sectionLabel = (s) => SECTION_LABELS[s] || s;

const AdFeatures = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [groups, setGroups] = useState([]);
    const [options, setOptions] = useState([]);
    const [section, setSection] = useState("motorbikes");
    const [groupId, setGroupId] = useState(null);
    const [draft, setDraft] = useState(null);      // Set of option ids, while editing
    const [search, setSearch] = useState("");
    const [newFeature, setNewFeature] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchData(ApiURLs.AD_FEATURES);
            const data = res?.data?.data || res?.data || {};
            setGroups(Array.isArray(data.groups) ? data.groups : []);
            setOptions(Array.isArray(data.options) ? data.options : []);
        } catch (e) {
            toast.error("Could not load the feature lists");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const sections = useMemo(() => {
        const seen = [];
        for (const g of groups) if (!seen.includes(g.section)) seen.push(g.section);
        return seen;
    }, [groups]);

    const sectionGroups = useMemo(
        () => groups.filter((g) => g.section === section),
        [groups, section]
    );

    // Nothing selected yet, or the selection belongs to another section.
    const current = useMemo(
        () => sectionGroups.find((g) => g.id === groupId) || sectionGroups[0] || null,
        [sectionGroups, groupId]
    );

    const chosen = useMemo(() => {
        if (draft && current && draft.groupId === current.id) return draft.ids;
        return new Set(current ? current.option_ids : []);
    }, [draft, current]);

    const dirty = Boolean(draft && current && draft.groupId === current.id);

    const setChosen = (next) => setDraft({ groupId: current.id, ids: next });

    const toggle = (id) => {
        const next = new Set(chosen);
        if (next.has(id)) next.delete(id); else next.add(id);
        setChosen(next);
    };

    const visibleOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        return options
            .filter((o) => o.status === 1 || chosen.has(o.id))
            .filter((o) => !q || o.name.toLowerCase().includes(q))
            // Ticked first, so a list of 293 is still readable while you edit one type.
            .sort((a, b) => {
                const ca = chosen.has(a.id) ? 0 : 1;
                const cb = chosen.has(b.id) ? 0 : 1;
                return ca - cb || a.name.localeCompare(b.name);
            });
    }, [options, search, chosen]);

    const save = async () => {
        if (!current || !dirty) return;
        setSaving(true);
        try {
            const res = await postData(
                { group_id: current.id, option_ids: [...chosen] },
                ApiURLs.AD_FEATURE_GROUP,
                false
            );
            if (res?.data?.status === 1) {
                toast.success(`Saved. ${chosen.size} features on ${current.title}.`);
                setDraft(null);
                await load();
            } else {
                toast.error(res?.data?.message || "Could not save");
            }
        } catch (e) {
            toast.error("Could not save");
        } finally {
            setSaving(false);
        }
    };

    const addFeature = async () => {
        const name = newFeature.trim();
        if (!name) return;
        setSaving(true);
        try {
            const res = await postData({ name }, ApiURLs.AD_FEATURE_OPTION, false);
            if (res?.data?.status === 1) {
                const id = res.data.data?.id;
                toast.success(res.data.data?.reused
                    ? `"${name}" already existed - ticked it here rather than making a second one.`
                    : `Added "${name}".`);
                setNewFeature("");
                await load();
                if (id && current) {
                    const next = new Set(chosen);
                    next.add(id);
                    setDraft({ groupId: current.id, ids: next });
                }
            } else {
                toast.error(res?.data?.message || "Could not add that feature");
            }
        } catch (e) {
            toast.error("Could not add that feature");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Advert Features
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                What a seller is asked to tick, for each type of vehicle. Pick a section,
                pick a type, tick the features that belong on it.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                A feature is shared across every type that uses it, so "ABS" here is the
                same "ABS" a buyer filters on. Unticking one removes it from this type
                only - adverts that already have it keep it.
            </Alert>

            {/* Sections */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {sections.map((s) => (
                    <Chip
                        key={s}
                        label={sectionLabel(s)}
                        color={s === section ? "primary" : "default"}
                        variant={s === section ? "filled" : "outlined"}
                        onClick={() => { setSection(s); setGroupId(null); setDraft(null); setSearch(""); }}
                    />
                ))}
            </Box>

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexDirection: { xs: "column", md: "row" } }}>
                {/* Types in this section */}
                <Paper variant="outlined" sx={{ p: 1, width: { xs: "100%", md: 300 }, flex: "0 0 auto", maxHeight: 640, overflowY: "auto" }}>
                    {sectionGroups.map((g) => (
                        <Box
                            key={g.id}
                            onClick={() => { setGroupId(g.id); setDraft(null); setSearch(""); }}
                            sx={{
                                p: 1.25, borderRadius: 1, cursor: "pointer",
                                bgcolor: current && g.id === current.id ? "action.selected" : "transparent",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        >
                            <Typography sx={{ fontWeight: g.body_type === "" ? 700 : 500, fontSize: 14 }}>
                                {g.title}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                {g.body_type === "" ? "shown on every type" : `type: ${g.body_type}`}
                                {" · "}{g.option_ids.length} features
                            </Typography>
                        </Box>
                    ))}
                    {!sectionGroups.length && (
                        <Typography sx={{ p: 2, fontSize: 14, color: "text.secondary" }}>
                            Nothing set up in this section yet.
                        </Typography>
                    )}
                </Paper>

                {/* The features on the selected type */}
                <Paper variant="outlined" sx={{ p: 2, flex: 1, width: "100%" }}>
                    {!current ? (
                        <Typography sx={{ color: "text.secondary" }}>Pick a type on the left.</Typography>
                    ) : (
                        <>
                            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                                    {current.title}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                                    {chosen.size} ticked
                                </Typography>
                                <Button
                                    variant="contained"
                                    disabled={!dirty || saving}
                                    onClick={save}
                                >
                                    {saving ? "Saving..." : dirty ? "Save changes" : "No changes"}
                                </Button>
                                {dirty && (
                                    <Button variant="text" disabled={saving} onClick={() => setDraft(null)}>
                                        Undo
                                    </Button>
                                )}
                            </Box>

                            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                <TextField
                                    size="small"
                                    label="Search features"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{ minWidth: 220 }}
                                />
                                <TextField
                                    size="small"
                                    label="Add a new feature"
                                    value={newFeature}
                                    onChange={(e) => setNewFeature(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") addFeature(); }}
                                    sx={{ minWidth: 240 }}
                                />
                                <Button variant="outlined" disabled={!newFeature.trim() || saving} onClick={addFeature}>
                                    Add
                                </Button>
                            </Box>

                            <Box sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                                maxHeight: 520, overflowY: "auto",
                            }}>
                                {visibleOptions.map((o) => (
                                    <FormControlLabel
                                        key={o.id}
                                        sx={{ mr: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={chosen.has(o.id)}
                                                onChange={() => toggle(o.id)}
                                            />
                                        }
                                        label={
                                            <span style={{ fontSize: 14 }}>
                                                {o.name}
                                                {o.uses > 0 && (
                                                    <span style={{ color: "#6c757d", fontSize: 12 }}>
                                                        {" "}&middot; on {o.uses} advert{o.uses === 1 ? "" : "s"}
                                                    </span>
                                                )}
                                            </span>
                                        }
                                    />
                                ))}
                                {!visibleOptions.length && (
                                    <Typography sx={{ p: 2, fontSize: 14, color: "text.secondary" }}>
                                        Nothing matches "{search}". Type it in "Add a new feature" to create it.
                                    </Typography>
                                )}
                            </Box>
                        </>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default AdFeatures;
